/**
 * @author      Michael Hettmer <mail@michael-hettmer.de>
 * @copyright   2019 Plusbyte UG (haftungsbeschränkt)
 * @license     {@link https://plusbyte.de Plusbyte License}
 */

import React, { useCallback, useContext, useState } from 'react';

import { useAuthDispatch } from '~/auth';
import LogoutDialog from '~/components/LogoutDialog';
import { hmrSafeContext } from '~/utils/hmrSafeContext';

interface LogoutDialogContextValue {
  openLogoutDialog: () => void;
}

const LogoutDialogContext = hmrSafeContext<LogoutDialogContextValue | null>(
  '__turfmaster_logout_dialog_ctx__',
  null,
);

export const useLogoutDialog = (): LogoutDialogContextValue => {
  const ctx = useContext(LogoutDialogContext);
  if (!ctx) {
    throw new Error('useLogoutDialog must be used within LogoutDialogProvider');
  }
  return ctx;
};

interface LogoutDialogProviderProps {
  children: React.ReactNode;
}

export const LogoutDialogProvider = ({ children }: LogoutDialogProviderProps): JSX.Element => {
  const [modalOpen, setModalOpen] = useState(false);
  const authDispatch = useAuthDispatch();

  const openLogoutDialog = useCallback(() => setModalOpen(true), []);

  const handleLogout = useCallback(() => {
    authDispatch({ type: 'logout' });
    setModalOpen(false);
    // Do not navigate away: protected routes rely on staying on the current URL so
    // ProtectedRouteGuard can show the login gate and preserve return-after-login behavior.
  }, [authDispatch]);

  return (
    <LogoutDialogContext.Provider value={{ openLogoutDialog }}>
      {children}
      <LogoutDialog
        modalOpen={modalOpen}
        onModalClose={() => setModalOpen(false)}
        onLogout={handleLogout}
      />
    </LogoutDialogContext.Provider>
  );
};
